import { useMemo } from 'react'
import type { Matcher } from 'react-day-picker'
import { useFormContext } from 'react-hook-form'
import type { EventTypesPublicGetEventTypeAvailabilities200 } from '@cocoonspace/api'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { Calendar } from '@cocoonspace/ui/components/calendar'
import { FormField } from '@cocoonspace/ui/components/form'
import { LoadingBar } from '@cocoonspace/ui/components/loading-bar'
import { useLingui } from '@lingui/react/macro'
import { format } from 'date-fns/format'
import { enUS as en } from 'date-fns/locale/en-US'
import { fr } from 'date-fns/locale/fr'

import { useAnswerCard } from './provider'
import type { BookingRequestAnswerFormData } from '.'

const getDisabledDays = (
	monthSlots: EventTypesPublicGetEventTypeAvailabilities200 | undefined,
	currentMonth: Date,
) => {
	const startOfMonth = dateTimeUtils(currentMonth).startOf('month')
	const daysInMonth = startOfMonth.daysInMonth()

	const disabledDays: Date[] = []

	for (let i = 0; i < daysInMonth; i++) {
		const currentDay = startOfMonth.add(i, 'day')
		const formatted = currentDay.format('YYYY-MM-DD')
		if (!monthSlots?.[formatted] || !monthSlots[formatted].length) {
			disabledDays.push(currentDay.toDate())
		}
	}

	return disabledDays
}

export const DatePicker = ({
	monthSlots,
	isLoading,
}: {
	monthSlots?: EventTypesPublicGetEventTypeAvailabilities200
	isLoading?: boolean
}) => {
	const form = useFormContext<BookingRequestAnswerFormData>()
	const { i18n } = useLingui()
	const { eventType, setCurrentMonth, currentMonth } = useAnswerCard()

	const disabled = useMemo<Matcher[]>(() => {
		const durationStart = dateTimeUtils.duration(eventType.bookingWindow.start)
		const durationEnd = dateTimeUtils.duration(eventType.bookingWindow.end)

		const before = dateTimeUtils().add(durationStart).toDate()
		const after = dateTimeUtils().add(durationEnd).toDate()

		const dayMapping: Record<string, number> = {
			sun: 0,
			mon: 1,
			tue: 2,
			wed: 3,
			thu: 4,
			fri: 5,
			sat: 6,
		}

		const dayOfWeek = Object.entries(eventType.availabilities)
			.filter(([, timeSlots]) => timeSlots.length === 0)
			.map(([dayKey]) => dayMapping[dayKey])

		const disabled = getDisabledDays(monthSlots, currentMonth)

		return [{ dayOfWeek }, { before, after }, ...disabled]
	}, [
		eventType.bookingWindow,
		eventType.availabilities,
		monthSlots,
		currentMonth,
	])

	return (
		<div className='relative min-w-0'>
			<LoadingBar isLoading={isLoading} />

			<FormField
				name='day'
				render={({ field }) => (
					<Calendar
						locale={{ en, fr }[i18n.locale as 'fr']}
						mode='single'
						disabled={disabled}
						selected={field.value ? new Date(field.value) : undefined}
						classNames={{
							cell: '!size-45px',
							button:
								'!text-xs rounded-full size-[45px] flex items-center justify-center cursor-pointer',
							head_cell: 'text-xs size-[45px] font-normal opacity-50',
						}}
						onMonthChange={setCurrentMonth}
						onDayClick={(date) => {
							if (!date) return
							const day = format(date, 'yyyy-MM-dd')
							form.resetField('start')
							field.onChange(day)
						}}
					/>
				)}
			/>
		</div>
	)
}
